import { Component } from "react";
import { Link } from "react-router-dom";
import { Sparkles, Home } from "lucide-react";

/**
 * Generic error boundary used to wrap fragile experiences (voice/audio,
 * media-recorder, framer-motion heavy screens) so a runtime error in a
 * single component does not blank the entire page.
 *
 * Renders a friendly fallback with a retry + go-home affordance.
 */
export class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    console.error("[ErrorBoundary]", error, info);
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (!this.state.hasError) return this.props.children;
    const msg = String(this.state.error?.message || this.state.error || "Algo se interrumpió");
    return (
      <div
        data-testid="error-boundary-fallback"
        className="min-h-screen bg-[#070708] text-neutral-100 flex items-center justify-center px-6"
      >
        <div className="max-w-md w-full text-center">
          <div className="w-20 h-20 mx-auto rounded-full bg-gradient-to-br from-[#d4af37]/20 to-purple-700/20 flex items-center justify-center border border-[#d4af37]/30 mb-6">
            <Sparkles className="w-10 h-10 text-[#d4af37]" />
          </div>
          <h1 className="font-serif text-2xl text-white mb-2">La energía se cortó por un instante</h1>
          <p className="text-neutral-400 text-sm mb-6">
            Algo interrumpió la sesión. Esto puede ocurrir si tu navegador bloqueó el audio o el micrófono.
            Puedes reintentar sin perder tu cuenta.
          </p>
          <p className="text-[11px] text-neutral-600 mb-6 font-mono break-words">{msg}</p>
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-3">
            <button
              type="button"
              onClick={this.handleRetry}
              data-testid="error-boundary-retry"
              className="btn-gold rounded-full px-6 py-3 uppercase tracking-[0.2em] text-xs font-semibold"
            >
              Reintentar
            </button>
            <Link
              to="/"
              data-testid="error-boundary-home"
              className="rounded-full px-6 py-3 uppercase tracking-[0.2em] text-xs font-semibold border border-white/15 text-neutral-300 hover:bg-white/5 inline-flex items-center justify-center gap-2"
            >
              <Home className="w-3.5 h-3.5" /> Ir al inicio
            </Link>
          </div>
        </div>
      </div>
    );
  }
}
